import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import StatusBadge from '../components/StatusBadge';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  UserPlusIcon,
  UserCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  BriefcaseIcon,
  ShieldCheckIcon,
  FunnelIcon,
  ArrowPathIcon,
  KeyIcon,
  CalculatorIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const Users = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tempPassword, setTempPassword] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    user_type: 'sales_agent',
    depot_id: '',
    employee_id: '',
    commission_basis: 'cases_sold', // 'cases_sold' or 'money_paid'
    commission_calculation: 'percentage', // 'percentage' or 'fixed'
    commission_value: 0,
    business_name: '',
    town: '',
    credit_limit: 0,
    payment_terms: 30,
    is_active: true
  });
  const [depots, setDepots] = useState([]);
  const [filters, setFilters] = useState({
    user_type: '',
    status: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 5,
    total: 0
  });

  useEffect(() => {
    fetchUsers();
    if (currentUser?.user_type === 'owner') {
      fetchDepots();
    }
  }, [pagination.page, pagination.per_page, filters]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
        user_type: filters.user_type || undefined,
        search: filters.search || undefined
      };

      // Remove undefined values
      Object.keys(params).forEach(key => 
        params[key] === undefined && delete params[key]
      );

      const response = await api.get('/users/', { params });
      setUsers(response.data.items || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total
      }));
    } catch (error) {
      toast.error('Failed to fetch users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepots = async () => {
    try {
      const response = await api.get('/depot/');
      setDepots(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch depots:', error);
    }
  };

  const handleCreateUser = async () => {
    try {
      const response = await api.post('/users/', formData);
      toast.success('User created successfully');
      setTempPassword(response.data.temp_password);
      setShowModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create user');
    }
  };

  const handleUpdateUser = async () => {
    console.log('Update user with data:', formData);
    try {
      await api.put(`/users/${selectedUser.id}`, formData);
      toast.success('User updated successfully');
      setShowModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await api.delete(`/users/${selectedUser.id}`);
      toast.success('User deactivated successfully');
      setShowDeleteDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to deactivate user');
    }
  };

  const handleResetPassword = async () => {
    try {
      const response = await api.post(`/auth/reset-password/${selectedUser.id}`);
      setTempPassword(response.data.temp_password);
      toast.success('Password reset successfully');
      setShowResetPasswordDialog(false);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      await api.put(`/users/${user.id}`, {
        is_active: !user.is_active
      });
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      phone_number: '',
      user_type: 'sales_agent',
      depot_id: '',
      employee_id: '',
      commission_basis: 'cases_sold',
      commission_calculation: 'percentage',
      commission_value: 0,
      business_name: '',
      town: '',
      credit_limit: 0,
      payment_terms: 30,
      is_active: true
    });
    setSelectedUser(null);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      phone_number: user.phone_number || '',
      user_type: user.user_type,
      depot_id: user.profile?.depot_id || '',
      employee_id: user.profile?.employee_id || '',
      commission_basis: user.profile?.commission_basis || 'cases_sold',
      commission_calculation: user.profile?.commission_calculation || 'percentage',
      commission_value: user.profile?.commission_value || 0,
      business_name: user.profile?.business_name || '',
      town: user.profile?.town || '',
      credit_limit: user.profile?.credit_limit || 0,
      payment_terms: user.profile?.payment_terms || 30,
      is_active: user.is_active
    });
    setShowModal(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      user_type: '',
      status: '',
      search: ''
    });
  };

  const getUserTypeIcon = (type) => {
    switch(type) {
      case 'owner':
        return <ShieldCheckIcon className="h-4 w-4 text-purple-500" />;
      case 'manager':
        return <BriefcaseIcon className="h-4 w-4 text-blue-500" />;
      case 'sales_agent':
        return <UserCircleIcon className="h-4 w-4 text-green-500" />;
      case 'shop_owner':
        return <BuildingOfficeIcon className="h-4 w-4 text-orange-500" />;
      default:
        return <UserCircleIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getUserTypeColor = (type) => {
    switch(type) {
      case 'owner':
        return 'bg-purple-100 text-purple-800';
      case 'manager':
        return 'bg-blue-100 text-blue-800';
      case 'sales_agent':
        return 'bg-green-100 text-green-800';
      case 'shop_owner':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCommissionText = (profile) => {
    if (!profile || !profile.commission_value) return 'No commission set';
    
    const basis = profile.commission_basis === 'cases_sold' ? 'per case sold' : 'of amount';
    const calculation = profile.commission_calculation === 'percentage' ? `${profile.commission_value}%` : `ZMW ${profile.commission_value}`;
    
    return `${calculation} ${basis}`;
  };

  const columns = [
    {
      header: 'User',
      accessorKey: 'full_name',
      cell: ({ row }) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
            {getUserTypeIcon(row.original.user_type)}
          </div>
          <div className="ml-4">
            <div className="font-medium text-gray-900">{row.original.full_name}</div>
            <div className="text-xs text-gray-500 flex items-center">
              <EnvelopeIcon className="h-3 w-3 mr-1" />
              {row.original.email}
            </div>
          </div>
        </div>
      )
    },
    {
      header: 'Contact',
      accessorKey: 'phone_number',
      cell: ({ row }) => (
        <div className="flex items-center">
          <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
          <span>{row.original.phone_number || '-'}</span>
        </div>
      )
    },
    {
      header: 'User Type',
      accessorKey: 'user_type',
      cell: ({ getValue }) => {
        const type = getValue();
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getUserTypeColor(type)}`}>
            {type.replace('_', ' ')}
          </span>
        );
      }
    },
    {
      header: 'Commission',
      accessorKey: 'profile',
      cell: ({ row }) => {
        if (row.original.user_type !== 'sales_agent' || !row.original.profile) return '-';
        return (
          <div className="text-xs">
            <span className="font-medium">{formatCommissionText(row.original.profile)}</span>
          </div>
        );
      }
    },
    {
      header: 'Status',
      accessorKey: 'is_active',
      cell: ({ getValue }) => (
        <StatusBadge status={getValue() ? 'active' : 'inactive'} />
      )
    },
    {
      header: 'Created',
      accessorKey: 'created_at',
      cell: ({ getValue }) => getValue() ? format(new Date(getValue()), 'dd MMM yyyy') : '-'
    },
    {
      header: 'Last Login',
      accessorKey: 'last_login',
      cell: ({ getValue }) => getValue() ? format(new Date(getValue()), 'dd MMM yyyy HH:mm') : 'Never'
    },
    {
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleEdit(row.original)}
            className="text-blue-600 hover:text-blue-900"
            title="Edit User"
            disabled={currentUser?.user_type !== 'owner' && currentUser?.id !== row.original.id}
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          
          {currentUser?.user_type === 'owner' && currentUser?.id !== row.original.id && (
            <>
              <button
                onClick={() => {
                  setSelectedUser(row.original);
                  setShowResetPasswordDialog(true);
                }}
                className="text-yellow-600 hover:text-yellow-900"
                title="Reset Password"
              >
                <KeyIcon className="h-5 w-5" />
              </button>
              
              <button
                onClick={() => handleToggleStatus(row.original)}
                className={row.original.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}
                title={row.original.is_active ? 'Deactivate' : 'Activate'}
              >
                {row.original.is_active ? <TrashIcon className="h-5 w-5" /> : <UserPlusIcon className="h-5 w-5" />}
              </button>
            </>
          )}
        </div>
      )
    }
  ];

  return (
    <div>
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">User Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage system users, their roles, and permissions.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:flex-none space-x-3">
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>
          {currentUser?.user_type === 'owner' && (
            <button
              type="button"
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add User
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
            <dd className="mt-1 text-2xl font-semibold text-gray-900">{pagination.total}</dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500 truncate">Active Users</dt>
            <dd className="mt-1 text-2xl font-semibold text-green-600">
              {users.filter(u => u.is_active).length}
            </dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500 truncate">Sales Agents</dt>
            <dd className="mt-1 text-2xl font-semibold text-blue-600">
              {users.filter(u => u.user_type === 'sales_agent').length}
            </dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500 truncate">Shop Owners</dt>
            <dd className="mt-1 text-2xl font-semibold text-orange-600">
              {users.filter(u => u.user_type === 'shop_owner').length}
            </dd>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mt-6 bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Filter Users</h3>
            <button
              onClick={clearFilters}
              className="text-sm text-primary-600 hover:text-primary-800"
            >
              Clear All
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">Search</label>
              <input
                type="text"
                placeholder="Search by name or email..."
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">User Type</label>
              <select
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={filters.user_type}
                onChange={(e) => handleFilterChange('user_type', e.target.value)}
              >
                <option value="">All Types</option>
                <option value="owner">Owner</option>
                <option value="manager">Manager</option>
                <option value="sales_agent">Sales Agent</option>
                <option value="shop_owner">Shop Owner</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={fetchUsers}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="mt-8">
        <DataTable
          data={users}
          columns={columns}
          loading={loading}
          pageSize={pagination.per_page}
          total={pagination.total}
          page={pagination.page}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          onPageSizeChange={(per_page) => setPagination(prev => ({ ...prev, per_page, page: 1 }))}
        />
      </div>

      {/* Create/Edit User Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
          setTempPassword('');
        }}
        title={selectedUser ? 'Edit User' : 'Create New User'}
        size="lg"
      >
        {tempPassword ? (
          <div className="text-center py-6">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <KeyIcon className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">User Created Successfully!</h3>
            <p className="text-sm text-gray-500 mb-4">
              Temporary password for <span className="font-medium">{formData.email}</span>
            </p>
            <div className="bg-gray-100 p-4 rounded-lg mb-4">
              <p className="text-2xl font-mono font-bold text-primary-600">{tempPassword}</p>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Please share this password with the user. They will be required to change it on first login.
            </p>
            <button
              onClick={() => {
                setTempPassword('');
                setShowModal(false);
                resetForm();
              }}
              className="btn-primary"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={(e) => {
            e.preventDefault();
            selectedUser ? handleUpdateUser() : handleCreateUser();
          }}>
            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">First Name *</label>
                    <input
                      type="text"
                      required
                      className="form-input"
                      value={formData.first_name}
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="form-label">Last Name *</label>
                    <input
                      type="text"
                      required
                      className="form-input"
                      value={formData.last_name}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="form-label">Email *</label>
                  <div className="relative">
                    <EnvelopeIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      required
                      className="form-input pl-10"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      disabled={!selectedUser}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="form-label">Phone Number</label>
                  <div className="relative">
                    <PhoneIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <input
                      type="tel"
                      className="form-input pl-10"
                      value={formData.phone_number}
                      onChange={(e) => setFormData({...formData, phone_number: e.target.value})}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="form-label">User Type *</label>
                  <select
                    required
                    className="form-input"
                    value={formData.user_type}
                    onChange={(e) => setFormData({...formData, user_type: e.target.value})}
                    disabled={!!selectedUser || currentUser?.user_type !== 'owner'}
                  >
                    <option value="sales_agent">Sales Agent</option>
                    <option value="manager">Manager</option>
                    <option value="shop_owner">Shop Owner</option>
                    {currentUser?.user_type === 'owner' && (
                      <option value="owner">Owner</option>
                    )}
                  </select>
                </div>

                {selectedUser && currentUser?.user_type === 'owner' && (
                  <div className="mt-4 flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                      Account is active
                    </label>
                  </div>
                )}
              </div>

              {/* Sales Agent Details with Commission Settings */}
              {formData.user_type === 'sales_agent' && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <CalculatorIcon className="h-5 w-5 mr-2 text-gray-500" />
                    Sales Agent Details & Commission Settings
                  </h3>
                  
                  <div>
                    <label className="form-label">Depot *</label>
                    <select
                      required
                      className="form-input"
                      value={formData.depot_id}
                      onChange={(e) => setFormData({...formData, depot_id: e.target.value})}
                    >
                      <option value="">Select Depot</option>
                      {depots.map(depot => (
                        <option key={depot.id} value={depot.id}>{depot.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mt-4">
                    <label className="form-label">Employee ID</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.employee_id}
                      onChange={(e) => setFormData({...formData, employee_id: e.target.value})}
                    />
                  </div>

                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-700 mb-3">Commission Settings</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Commission Basis *</label>
                        <select
                          required
                          className="form-input"
                          value={formData.commission_basis}
                          onChange={(e) => setFormData({...formData, commission_basis: e.target.value})}
                        >
                          <option value="cases_sold">Based on Cases Sold</option>
                          <option value="money_paid">Based on Money Paid</option>
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                          {formData.commission_basis === 'cases_sold' 
                            ? 'Commission calculated per case sold' 
                            : 'Commission calculated based on payment amount'}
                        </p>
                      </div>

                      <div>
                        <label className="form-label">Calculation Type *</label>
                        <select
                          required
                          className="form-input"
                          value={formData.commission_calculation}
                          onChange={(e) => setFormData({...formData, commission_calculation: e.target.value})}
                        >
                          <option value="percentage">Percentage (%)</option>
                          <option value="fixed">Fixed Amount (ZMW)</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="form-label">
                        {formData.commission_calculation === 'percentage' ? 'Commission Rate (%) *' : 'Commission Amount (ZMW) *'}
                      </label>
                      <div className="relative">
                        {formData.commission_calculation === 'fixed' && (
                          <span className="absolute left-3 top-2.5 text-gray-500">ZMW</span>
                        )}
                        <input
                          type="number"
                          step={formData.commission_calculation === 'percentage' ? "0.1" : "0.01"}
                          min="0"
                          required
                          className={`form-input ${formData.commission_calculation === 'fixed' ? 'pl-12' : ''}`}
                          value={formData.commission_value}
                          onChange={(e) => setFormData({...formData, commission_value: parseFloat(e.target.value) || 0})}
                        />
                      </div>
                      <p className="mt-2 text-sm text-gray-600 bg-blue-50 p-2 rounded">
                        <span className="font-medium">Example:</span>{' '}
                        {formData.commission_basis === 'cases_sold' 
                          ? `For every case sold, agent earns ${formData.commission_calculation === 'percentage' ? `${formData.commission_value}% of the case price` : `ZMW ${formData.commission_value}`}`
                          : `Agent earns ${formData.commission_calculation === 'percentage' ? `${formData.commission_value}% of total sale amount` : `ZMW ${formData.commission_value} per sale`}`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Shop Owner Details */}
              {formData.user_type === 'shop_owner' && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Shop Owner Details</h3>
                  
                  <div>
                    <label className="form-label">Business Name *</label>
                    <input
                      type="text"
                      required
                      className="form-input"
                      value={formData.business_name}
                      onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                    />
                  </div>

                  <div className="mt-4">
                    <label className="form-label">Town *</label>
                    <input
                      type="text"
                      required
                      className="form-input"
                      value={formData.town}
                      onChange={(e) => setFormData({...formData, town: e.target.value})}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="form-label">Credit Limit (ZMW)</label>
                      <input
                        type="number"
                        min="0"
                        step="100"
                        className="form-input"
                        value={formData.credit_limit}
                        onChange={(e) => setFormData({...formData, credit_limit: parseFloat(e.target.value)})}
                      />
                    </div>
                    <div>
                      <label className="form-label">Payment Terms (days)</label>
                      <input
                        type="number"
                        min="1"
                        className="form-input"
                        value={formData.payment_terms}
                        onChange={(e) => setFormData({...formData, payment_terms: parseInt(e.target.value)})}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  {selectedUser ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </form>
        )}
      </Modal>

      {/* Reset Password Confirmation */}
      <ConfirmDialog
        isOpen={showResetPasswordDialog}
        onClose={() => setShowResetPasswordDialog(false)}
        onConfirm={handleResetPassword}
        title="Reset Password"
        message={`Are you sure you want to reset the password for ${selectedUser?.full_name}? A new temporary password will be generated.`}
        confirmText="Reset Password"
        type="warning"
      />

      {/* Delete/Deactivate Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteUser}
        title="Deactivate User"
        message={`Are you sure you want to deactivate ${selectedUser?.full_name}? They will no longer be able to access the system.`}
        confirmText="Deactivate"
        type="danger"
      />
    </div>
  );
};

export default Users;